/**
 * Upload File Mutation Hooks
 *
 * React Query mutation hooks for file upload operations
 */

'use client';

import {
  useMutation,
  useQueryClient,
  type UseMutationResult
} from '@tanstack/react-query';
import type {
  MatterFile,
  FileListResponse,
  FileType,
  FileStatus,
  GetPresignedUrlRequest
} from '../types';
import { createFileErrorHandler, shouldRetry, validateFile } from './helpers';
import { buildUploadCompleteNaming } from '../upload-naming';
import { uploadFileToUrl, isAbortError } from '@/lib/uploads';
import {
  fileKeys,
  contactKeys,
  taskKeys,
  timelineKeys,
  institutionKeys
} from '@/lib/query-keys';

// ============================================================================
// UPLOAD FILE (SERVER ACTION)
// ============================================================================

/**
 * Hook to upload a file using server action
 *
 * @returns Mutation object for uploading files
 *
 * @example
 * ```tsx
 * const uploadMutation = useUploadFile();
 * const fileInput = document.querySelector('input[type="file"]');
 * uploadMutation.mutate({
 *   matterId: 1,
 *   file: fileInput.files[0],
 *   fileType: 'additional-document'
 * });
 * ```
 */
export function useUploadFile(): UseMutationResult<
  MatterFile,
  unknown,
  {
    matterId: number;
    file: File;
    fileType?: string;
    isCertifiedCopy?: boolean;
    analysisRequested?: boolean;
    displayName?: string;
    contactId?: number;
    institutionId?: number;
    institutionFormId?: number;
    signatoryContactId?: number;
    inventoryItemId?: number;
    attachmentId?: number;
    metadata?: Record<string, unknown>;
    /** Real S3-PUT progress, 0–100. Threaded through the shared primitive. */
    onProgress?: (percent: number) => void;
    /** Cancel the in-flight upload. Aborting rejects with an `AbortError`. */
    signal?: AbortSignal;
  },
  { placeholderId: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      matterId,
      file,
      fileType = 'additional-document',
      isCertifiedCopy,
      analysisRequested,
      displayName,
      contactId,
      institutionId,
      institutionFormId,
      signatoryContactId,
      inventoryItemId,
      attachmentId,
      metadata,
      onProgress,
      signal
    }) => {
      // Validate file before upload
      validateFile(file, fileType);
      // Bail before the presign round-trip if already cancelled.
      if (signal?.aborted)
        throw new DOMException('Upload aborted', 'AbortError');

      // Import server actions
      const { getPresignedUrlAction, uploadCompleteAction } =
        await import('../actions');

      // Step 1: Get presigned URL from server action with all fields
      // Build metadata object with institution fields
      const presignedMetadata: Record<string, unknown> = {
        ...(metadata || {})
      };

      // Remove contactId from metadata if it exists (it should be at root level)
      // and ensure all metadata values are strings as required by S3
      if (presignedMetadata.contactId !== undefined) {
        delete presignedMetadata.contactId;
      }

      // Add institution fields to metadata if they exist
      if (institutionId !== undefined) {
        presignedMetadata.institutionId = institutionId;
      }
      if (institutionFormId !== undefined) {
        presignedMetadata.institutionFormId = institutionFormId;
      }
      if (signatoryContactId !== undefined) {
        presignedMetadata.signatoryContactId = signatoryContactId;
      }
      if (inventoryItemId !== undefined) {
        presignedMetadata.inventoryItemId = inventoryItemId;
      }

      // Coerce numeric metadata fields to integers for backend validation.
      // The backend DTO requires institutionId, institutionFormId, etc. as integers.
      const numericMetadataKeys = new Set([
        'institutionId',
        'institutionFormId',
        'signatoryContactId',
        'inventoryItemId'
      ]);
      Object.keys(presignedMetadata).forEach((key) => {
        if (
          presignedMetadata[key] !== undefined &&
          presignedMetadata[key] !== null
        ) {
          if (numericMetadataKeys.has(key)) {
            presignedMetadata[key] = Number(presignedMetadata[key]);
          }
        }
      });

      const presignedUrlPayload: GetPresignedUrlRequest = {
        fileType: fileType as FileType,
        isCertifiedCopy: isCertifiedCopy === true, // Ensure boolean
        matterId, // Always include matterId - required for primary ID uploads to associate with contact
        ...(contactId && { contactId }), // Add contactId if provided (not in metadata for non-institution forms)
        ...(attachmentId && { attachmentId }), // Add attachmentId for correspondence attachments
        ...(analysisRequested === true && { analysisRequested: true }), // Opt typed upload into AI analysis (LF-2797)
        // LF-4019: auto-enrol additional-document AND correspondence-attachment uploads for type
        // reclassification. The backend self-gates (only acts when the matter is missing a will/death-cert slot
        // and the file classifies HIGH-confidence as one of those), so enabling it broadly here is safe and
        // decoupled from any specific upload surface. correspondence-attachment is included so a will / death
        // certificate uploaded straight onto a discovery correspondence still fills the matter's primary slot.
        ...(['additional-document', 'correspondence-attachment'].includes(
          fileType
        ) && {
          reclassify: true
        }),
        ...(Object.keys(presignedMetadata).length > 0 && {
          metadata: presignedMetadata
        }) // Add metadata if not empty
      };

      // Debug log to verify payload
      console.log(
        '[useUploadFile] Presigned URL request payload:',
        presignedUrlPayload
      );
      console.log(
        '[useUploadFile] Has attachmentId?',
        !!attachmentId,
        'Value:',
        attachmentId
      );

      const presignedResult = await getPresignedUrlAction(presignedUrlPayload);

      console.log('[useUploadFile] Presigned result:', {
        success: presignedResult.success,
        error: presignedResult.error
      });

      if (!presignedResult.success || !presignedResult.data) {
        throw new Error(presignedResult.error || 'Failed to get upload URL');
      }

      const { fileId, presignedUrl } = presignedResult.data;

      // Step 2: Upload to S3 (client-side)
      // This must be done client-side as server actions can't handle File objects
      // Real S3 PUT via the shared progress-capable primitive (LF-4771).
      await uploadFileToUrl(presignedUrl, file, {
        contentType: file.type || 'application/octet-stream',
        onProgress: (p) => onProgress?.(p.percent),
        signal
      });

      // Step 3: Complete upload with server action. `buildUploadCompleteNaming`
      // owns the filename-vs-label rule for both upload paths — see
      // ../upload-naming.ts.
      const completeResult = await uploadCompleteAction({
        fileId,
        ...buildUploadCompleteNaming(file.name, displayName),
        matterId
      });

      if (!completeResult.success) {
        throw new Error(completeResult.error || 'Failed to complete upload');
      }

      // Step 4: Update metadata if needed (displayName is sent with the upload-complete above)
      // Only update if we have isCertifiedCopy flag that wasn't sent in presigned request
      // Note: The presignedUrl request already includes isCertifiedCopy, so this might be redundant
      // but we keep it for consistency

      return completeResult.data as MatterFile;
    },

    // Retry configuration (less aggressive for uploads)
    retry: (failureCount, error) => {
      if (failureCount >= 2) return false;
      return shouldRetry(failureCount, error);
    },
    retryDelay: (attemptIndex) => Math.min(2000 * 2 ** attemptIndex, 30000),

    // Optimistic update - show placeholder while uploading
    onMutate: async ({ matterId, file, displayName }) => {
      // Cancel any in-flight queries
      await queryClient.cancelQueries({
        queryKey: fileKeys.forMatter(matterId)
      });

      // Create a temporary placeholder ID
      const placeholderId = `temp-${Date.now()}`;

      // Get current files
      const previousFiles = queryClient.getQueryData<FileListResponse>(
        fileKeys.forMatter(matterId)
      );

      // Add placeholder file to the list
      if (previousFiles) {
        const placeholderFile: MatterFile = {
          id: -1, // Temporary ID
          originalFilename: file.name,
          // Mirrors the server's own read fallback (displayName ?? originalFilename),
          // so the placeholder card renders the same label the saved file will.
          displayName: displayName || file.name,
          status: 'processing' satisfies FileStatus,
          s3Key: placeholderId,
          mimeType: file.type,
          sizeBytes: file.size,
          fileType: 'additional-document',
          isCertifiedCopy: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          matterId
        };

        queryClient.setQueryData<FileListResponse>(
          fileKeys.forMatter(matterId),
          {
            ...previousFiles,
            files: [placeholderFile, ...previousFiles.files],
            total: previousFiles.total + 1
          }
        );
      }

      return { placeholderId };
    },

    onSuccess: (newFile, variables) => {
      // Remove placeholder and add real file
      const currentFiles = queryClient.getQueryData<FileListResponse>(
        fileKeys.forMatter(variables.matterId)
      );

      if (currentFiles) {
        queryClient.setQueryData<FileListResponse>(
          fileKeys.forMatter(variables.matterId),
          {
            ...currentFiles,
            files: currentFiles.files.map((f) => (f.id === -1 ? newFile : f))
          }
        );
      }

      // Invalidate related queries to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: fileKeys.forMatter(variables.matterId)
      });

      // Success toast is handled by the calling component
      // Don't show duplicate toast here
    },

    onError: (error, variables, _context) => {
      // Remove placeholder on error
      const currentFiles = queryClient.getQueryData<FileListResponse>(
        fileKeys.forMatter(variables.matterId)
      );

      if (currentFiles) {
        queryClient.setQueryData<FileListResponse>(
          fileKeys.forMatter(variables.matterId),
          {
            ...currentFiles,
            files: currentFiles.files.filter((f) => f.id !== -1),
            total: currentFiles.total - 1
          }
        );
      }

      // A user cancel isn't a failure — the placeholder is already rolled back
      // above, so just stay silent. Real errors still toast.
      if (!isAbortError(error)) {
        // Use centralized error handler which avoids duplicate toasts
        createFileErrorHandler('upload')(error);
      }
    },

    onSettled: (data, error, variables) => {
      // Always refetch to ensure consistency
      queryClient.invalidateQueries({
        queryKey: fileKeys.forMatter(variables.matterId)
      });

      // If this was an ID document upload, invalidate contacts to show the new ID
      if (variables.contactId) {
        queryClient.invalidateQueries({
          queryKey: contactKeys.forMatter(variables.matterId)
        });
        queryClient.invalidateQueries({
          queryKey: contactKeys.detail(variables.contactId)
        });
      }

      // Invalidate tasks and history as they depend on document status
      // This is especially important for crucial documents (will, death certificate, grant of probate)
      queryClient.invalidateQueries({
        queryKey: taskKeys.dashboard(variables.matterId, 0)
      });
      queryClient.invalidateQueries({
        queryKey: taskKeys.dashboard(variables.matterId, 1)
      });
      queryClient.invalidateQueries({
        queryKey: timelineKeys.forMatter(variables.matterId)
      });
      queryClient.invalidateQueries({
        queryKey: timelineKeys.history(variables.matterId)
      });

      // If this was an institution form upload, invalidate forms and institution-specific tasks
      if (
        variables.institutionId ||
        variables.institutionFormId ||
        variables.fileType?.includes('institution-form')
      ) {
        // Invalidate institution forms cache
        queryClient.invalidateQueries({
          queryKey: institutionKeys.allForms(variables.matterId)
        });

        // Invalidate institution-specific forms if we have an institutionId
        if (variables.institutionId) {
          queryClient.invalidateQueries({
            queryKey: institutionKeys.forms(
              variables.matterId,
              variables.institutionId
            )
          });

          // Invalidate institution-specific tasks
          queryClient.invalidateQueries({
            queryKey: taskKeys.forInstitution(
              variables.matterId,
              variables.institutionId
            )
          });

          // Invalidate institution-specific task dashboard (for next steps on institution view)
          queryClient.invalidateQueries({
            queryKey: taskKeys.institutionDashboard(variables.matterId)
          });
        }

        // Invalidate all matter tasks as form upload may complete tasks
        queryClient.invalidateQueries({
          queryKey: taskKeys.forMatter(variables.matterId)
        });

        // CRITICAL: Invalidate task dashboard data for next steps card
        // This ensures next steps update immediately after form upload
        // Use predicate to match both aggregated and non-aggregated formats
        queryClient.invalidateQueries({
          queryKey: ['matter', variables.matterId],
          predicate: (query) => {
            // Match any tasks-dashboard-data query for this matter
            // Handles both ['matter', matterId, 0, 'tasks-dashboard-data'] and
            // ['matter', matterId, 1, 'tasks-dashboard-data'] formats
            return (
              query.queryKey[0] === 'matter' &&
              query.queryKey[1] === variables.matterId &&
              query.queryKey[3] === 'tasks-dashboard-data'
            );
          }
        });
      }
    }
  });
}
